<template>
  <action-bar
    @logoAction="home()"
    :user-data="userData"
    :hasNotifications="hasNotifications"
  >
    <template #title>
      {{ title }}
    </template>
    <template #dropdown-user>
      <div>
        <h4 class="usage-title">{{ $t("profile.menu.usage") }}</h4>
        <p class="usage-row">
          {{ $t("profile.menu.mvn_processing") }}:
          <span class="usage-value"
            >{{ usageMinutes }} {{ $t("profile.menu.minutes") }}</span
          >
        </p>

        <hr />
        <div class="inline-quota" v-if="hasNotifications">
          <p class="message">{{ $t("quota.avatar.message") }}</p>
          <a class="upgrade-link" :href="portalBilling.url" target="_blank">{{
            $t("quota.avatar.upgrade_link_title")
          }}</a>
        </div>
        <ul>
          <li v-if="!isAuthenticated">
            <router-link :to="{ name: 'login' }">{{
              $t("profile.menu.login")
            }}</router-link>
          </li>
          <li v-if="isAuthenticated">
            <router-link :to="{ name: 'messages' }">
              {{ $t("profile.menu.messages") }}
            </router-link>
          </li>
          <li v-if="isAuthenticated">
            <router-link :to="{ name: 'folders-overview' }">{{
              $t("profile.menu.storage")
            }}</router-link>
          </li>
          <li v-if="isAuthenticated">
            <a href="#" @click="doLogout()">{{ $t("profile.menu.logout") }}</a>
          </li>
        </ul>
        <hr />
        <label>{{ $t("app.language") }}</label>
        <multiselect
          v-model="$i18n.locale"
          :options="langs"
          :showLabels="false"
          :placeholder="$t('app.language_placeholder')"
        ></multiselect>
      </div>
    </template>
    <template #dropdown-apps>
      <div v-if="isAuthenticated">
        <ul>
          <li v-for="app in filteredApplications" :key="app.label">
            <a
              v-if="app.type === 'portal'"
              :href="app.url"
              :class="{ bold: isCurrent(app.label) }"
              :title="$t(app.label)"
              >{{ $t(app.label) }}</a
            >
          </li>
        </ul>
      </div>
    </template>
  </action-bar>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { translate } from "@/i18n";
import { Account, Configuration } from "@/motion-cloud";
import {
  SUBSCRIPTION_MESSAGE,
  SUBSCRIPTION_QUOTA,
  USAGE_QUOTA
} from "@/graphql/user";
import { Subscription } from "apollo-client/util/Observable";
import UserModule from "@/store/modules/user";
import { formatTimeLeft, formatDurationInMinutes } from "@/utils/time-helpers";

const user = namespace("user");
const auth = namespace("auth");

@Component
export default class PageHeader extends Vue {
  @Prop({ default: translate("app.title") })
  readonly title!: string;

  public langs: Array<string> = ["en-US", "nl-NL"];

  public usageMinutes = 0;
  public minutesLabel = "";

  @user.State
  public account!: Account;

  @auth.Getter
  public isAuthenticated!: boolean;

  @user.Getter
  public applications!: Configuration[];

  @user.Getter
  public portalHome!: Configuration;

  @user.Getter("quotaLimitReached")
  hasNotifications!: UserModule["quotaLimitReached"];

  @user.Mutation
  setQuota!: UserModule["setQuota"];

  @user.Getter("portalBilling")
  portalBilling!: Configuration;

  messageReceivedSubscription!: Subscription;
  quotaUpdatedSubscription!: Subscription;

  get userData() {
    return this.isAuthenticated ? this.account : {};
  }

  created() {
    if (!this.isAuthenticated) {
      return;
    }
    this.subscribeToMessages();
    this.subscribeToQuotaUpdate();
    this.fetchQuota();
  }

  get filteredApplications(): Configuration[] {
    return this.applications.filter(application => {
      if (
        application.permissions.length === 0 &&
        !application.roles.system_admin &&
        !application.roles.organization_admin
      ) {
        return true;
      } else {
        const hasPermissions = application.permissions.reduce(
          (granted, permission) => {
            return (
              this.account.permissions.indexOf(permission) !== -1 && granted
            );
          },
          true
        );
        let hasRole = false;
        if (
          (!application.roles.system_admin &&
            !application.roles.organization_admin) ||
          (application.roles.system_admin && this.account.roles.system_admin) ||
          (application.roles.organization_admin &&
            this.account.roles.organization_admin)
        ) {
          hasRole = true;
        }
        return hasPermissions && hasRole;
      }
    });
  }

  private async fetchQuota() {
    const { data } = await this.$apollo
      .query({
        query: USAGE_QUOTA,
        fetchPolicy: "no-cache"
      })
      .catch(() => {
        return {
          data: {
            organization: {
              quotas: []
            }
          }
        };
      });

    const seconds = data.organization.quotas.find(
      (quota: any) => quota.quotum_group === "seconds"
    );
    this.minutesLabel = seconds
      ? formatTimeLeft(seconds.usage_quotum - seconds.usage_used)
      : "-";

    this.usageMinutes = Number(formatDurationInMinutes(seconds.usage_used));
    this.setQuota(data.organization.quotas);
  }

  /**
   * Handle subscription to organization quota updates
   * @private
   */
  private async subscribeToQuotaUpdate() {
    const observer = await this.$apollo.subscribe({
      query: SUBSCRIPTION_QUOTA,
      fetchPolicy: "no-cache"
    });

    this.quotaUpdatedSubscription = observer.subscribe(() => {
      this.fetchQuota();
    });
  }

  private async subscribeToMessages() {
    const observer = await this.$apollo.subscribe({
      query: SUBSCRIPTION_MESSAGE,
      fetchPolicy: "no-cache"
    });

    this.messageReceivedSubscription = observer.subscribe(response => {
      const message = response.data.messageReceived.message.message;

      if (
        message.indexOf("could not be processed") !== -1 ||
        message.indexOf("failed to process") !== -1
      ) {
        this.$store.commit("upload/isProcessing", false);
      }

      if (message.indexOf("failed to process") !== -1) {
        this.$snotify.error(message);
      } else if (message.indexOf("warning:") !== -1) {
        this.$snotify.confirm(
          `${message}  (Click 'View all messages' to see your message history)`,
          {
            buttons: [
              {
                text: "View all messages",
                action: (toast: any) => {
                  this.$snotify.remove(toast.id);
                  this.$router.push({ name: "messages" });
                }
              },
              {
                text: "Ok",
                bold: true
              }
            ]
          }
        );
      } else {
        this.$snotify.info(message);
      }
    });
  }

  private home() {
    window.location.href = this.portalHome.url;
  }

  private doLogout() {
    this.$store.dispatch("auth/logout");
  }

  isCurrent(label: string) {
    return label.includes("reprocessing-portal");
  }

  beforeDestroy() {
    if (this.messageReceivedSubscription) {
      this.messageReceivedSubscription.unsubscribe();
    }
    if (this.quotaUpdatedSubscription) {
      this.quotaUpdatedSubscription.unsubscribe();
    }
  }
}
</script>

<style scoped lang="scss">
.logo-wide {
  cursor: pointer;
  background-image: url(../../assets/xsens-logo-widex2.png);
  background-size: 164px;
  background-position: center left;
  background-repeat: no-repeat;
  width: 183px;
  height: 83px;
  margin: 5px 0;

  @include media-breakpoint-down(xs) {
    background-position-x: 8px;
    width: 58px;
    margin: 0;
  }
}

.bold {
  font-weight: bold;
}

.logo-sub {
  color: #333;
  font-family: $headings-font-family;
  font-size: 21px;
  font-weight: 300;
  letter-spacing: 0.27px;

  position: relative;
  top: 2px;

  @include media-breakpoint-down(xs) {
    padding-left: 10px;
  }
}

.logo-line {
  height: 16px;
  width: 1px;
  background-color: #595a59;
  margin-right: 20px;

  @include media-breakpoint-down(xs) {
    display: none;
  }
}

.inline-quota {
  .message {
    font-size: 10px;
    color: $coral;
    margin-bottom: 0;
  }
  .upgrade-link {
    font-size: 10px;
    color: $link-blue;
    text-decoration: underline;
  }
}

.usage-title {
  margin: 0;
  font-weight: bold;
}

.usage-row {
  font-family: $headings-font-family;
  font-size: 1.7rem;
}

.usage-value {
  color: $shell;
}
</style>
